import React from "react";
import Link from "gatsby-link";
import styled from "styled-components";
import { H3, H4, H5, Description } from "./typography";
import AspectImage from "./atoms/AspectImage";
import { r, slugify } from "../utils";
import {
  HideTablet,
  HideDesktop,
  HideMobile,
  ShowMobile
} from "../components/blocks/Responsive";

const FeatureDescription = styled.div`
  position: absolute;
  left: 5rem;
  right: 8rem;
  bottom: 3rem;
  top: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  ${props => props.theme.media.tablet`
    left: 2rem;
    bottom: .5rem;
    right: 2rem;
  `};

  ${props => props.theme.media.mobile`
    left: 2rem;
    bottom: .5rem;
    right: 2rem;
  `};
`;

const PostType = Description.extend`
  display: inline-block;
  padding: 0;
  padding-right: 3rem;
`;

const PostDate = Description.extend`
  display: inline-block;
  font-weight: 100;
  font-size: 0.75rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 0;
  color: #888;
`;

const Thumbnail = styled.div`
  display: flex;
  min-height: 9rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  :last-child {
    border-bottom: none;
  }
  ${props => props.theme.media.desktop`
    height: auto;
  `};
  ${props => props.theme.media.card`
    flex-direction: column;
    height: auto;
    width: auto;
  `};
`;

const ThumbnailBody = styled.div`
  flex: 3;
  padding: 0.5rem 2rem 0;
  min-width: 0;
`;

const ThumbnailImage = styled.div`
  display: flex;
  flex: 1;
`;

const Title = styled.div`
  padding-bottom: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InlineH5 = H5.extend`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PostTitle = ({ color, post }) =>
  <Title>
    <InlineH5 color={color}>
      <b style={{ paddingRight: ".15rem", fontWeight: 600 }}>
        {post.title}
      </b>
      {post.subtitle}
    </InlineH5>
  </Title>;

const PostPreview = Description.extend`
  font-size: 0.875rem;
  line-height: 1.5;
  color: #555;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-align: left;
  margin-top: 0.25rem;
  padding-bottom: 0;
`;

const ClearLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

export const PostThumbnail = ({ post, index }) =>
  <ClearLink to={`/news/${post.id}#${slugify(post.title)}`}>
    <Thumbnail>
      <ThumbnailImage>
        <HideDesktop>
          <AspectImage
            direction="vertical"
            src={post.headerImage.responsiveResolution.src}
          />
        </HideDesktop>
        <HideTablet>
          <AspectImage
            direction="horizontal"
            src={post.headerImage.responsiveResolution.src}
          />
        </HideTablet>
      </ThumbnailImage>
      <ThumbnailBody>
        <PostDate serif>
          {post.publishDate.split(" ").slice(0, 3).join(" ")}
        </PostDate>
        <PostTitle color="dark" post={post} />
        <PostPreview>
          {post.body.body
            .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
            .replace(/\n/, "  ")
            .replace(/[^0-9,.\-\ A-Za-z]/g, "")}
        </PostPreview>
      </ThumbnailBody>
    </Thumbnail>
  </ClearLink>;

const Meta = styled.div`
  display: flex;
  align-items: center;
  paddingBottom: .5rem;
  ${props => props.theme.media.mobile`
    display: none;
  `};
`;

const FeatureTitle = H3.extend`${r("font-size", "1.875rem", "1rem")};`;

const PostMeta = ({ date }) =>
  <Meta>
    <PostType color="white">Featured</PostType>
    <PostDate serif color="white">
      {date.split(" ").slice(0, 3).join(" ")}
    </PostDate>
  </Meta>;

export const PostLink = ({
  post: { id, title, subtitle, publishDate, headerImage },
  children
}) =>
  <Link
    to={`/news/${id}#${slugify(title)}`}
    style={{
      textDecoration: "none",
      color: "inherit"
    }}
  >
    {children}
  </Link>;

export const PostFeature = ({
  post: { id, title, subtitle, publishDate, headerImage }
}) =>
  <ClearLink to={`/news/${id}#${slugify(title)}`}>
    <AspectImage src={headerImage && headerImage.responsiveResolution.src}>
      <HideMobile>
        <FeatureDescription>
          <PostMeta date={publishDate} />
          <FeatureTitle color="white">
            {title + " " + subtitle}
          </FeatureTitle>
        </FeatureDescription>
      </HideMobile>
    </AspectImage>
    <ShowMobile>
      <H4 style={{ padding: "2rem .5rem 0 .5rem" }}>
        {title + " " + subtitle}
      </H4>
    </ShowMobile>
  </ClearLink>;

export const PostBody = styled.div`
  p,
  li {
    max-width: 680px;
    padding-bottom: 1rem;
    font-size: 1.2rem;
    line-height: 1.6;
  }
  ul {
    padding-left: 1rem;
  }
  a {
    text-decoration: none;
  }
  b,
  strong {
    font-weight: 500;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 1.4rem;
    line-height: 1.2;
    padding-bottom: .8rem;
    font-family: Palantino;
    font-weight: 500;
  }
  img {
    padding: 2rem 0;
    width: 100%;
    position: relative;
    right: -4rem;
    left: 0rem;
  }
  blockquote {
    margin: 3rem .5rem;
  }
  blockquote p {
    font-size: 1.8rem;
    line-height: 1.4;
    padding: 2rem 0;
  }
  blockquote:before,
  blockquote:after {
    content: ' ';
    width: 2rem;
    height: 4px;
    position: absolute;
    background: ${({ theme }) => theme.colors.teal};
  }

  padding-left: 5rem;
  padding-right: 6rem;
  @media (max-width: 720px) {
    padding-left: .5rem;
    padding-right: .5rem;
  }
`;



// WEBPACK FOOTER //
// ./src/components/Post.js